import React, { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import {
  type RoomRateGridRow,
  RoomRateSelection as RoomRateSelectionGrid,
} from '~/components/Vendors/SalesforceSurcharges/components/RoomRateSelection';

import { reservationQueryKeys } from '~/hooks/apis/reservations/reservationQueryKeys';
import { useApplyBlackoutDates } from '~/hooks/apis/stopsell/useApplyBlackoutDates';

import { pluralizeToString } from '~/utils/stringUtils';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import type { DeletedBlackoutRange } from './blackoutReconciliation';
import {
  applyBlockExclusions,
  buildWizardApplyPayload,
  buildWizardApplyPlan,
  getDeletedRangeKey,
} from './buildBlackoutWizardApplyPlan';
import { BlackoutRecordSelection } from './components/BlackoutRecordSelection';
import { BlackoutWizardSummary } from './components/BlackoutWizardSummary';
import type { SalesforceBlackoutDate } from './types';

interface Props {
  blackoutDates: Array<SalesforceBlackoutDate>;
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  onClose: () => void;
  propertyId: string;
  roomRateSelections: Array<RoomRateSelection>;
  vendorId: string;
}

const stepsList = ['Select blackout dates', 'Select room rates', 'Summary'];

const SELECT_BLACKOUT_DATES_STEP = 0;
const SELECT_ROOM_RATES_STEP = 1;
const SUMMARY_STEP = 2;

export default function SalesforceBlackoutDatesWizardModal({
  blackoutDates,
  blackoutDatesByRoomRate,
  onClose,
  propertyId,
  roomRateSelections,
  vendorId,
}: Props) {
  const queryClient = useQueryClient();
  const applyBlackoutDates = useApplyBlackoutDates(propertyId, 'blackout dates');

  const [step, setStep] = useState(SELECT_BLACKOUT_DATES_STEP);
  const [stepError, setStepError] = useState<number | null>(null);
  const [stepErrorMessage, setStepErrorMessage] = useState<string | null>(null);
  const [selectedBlackoutDates, setSelectedBlackoutDates] = useState<Array<SalesforceBlackoutDate>>([]);
  const [selectedRoomRates, setSelectedRoomRates] = useState<Array<RoomRateGridRow>>([]);
  const [excludedBlockKeys, setExcludedBlockKeys] = useState<Set<string>>(new Set());

  const roomRateRows: Array<RoomRateGridRow> = useMemo(
    () =>
      roomRateSelections.map(({ roomType, roomRate }) => ({
        propertyId,
        uniqueId: roomRate.id,
        id: roomRate.id,
        roomType,
        roomRate,
      })),
    [roomRateSelections, propertyId],
  );

  const plans = useMemo(
    () => buildWizardApplyPlan(selectedBlackoutDates, selectedRoomRates, blackoutDatesByRoomRate),
    [selectedBlackoutDates, selectedRoomRates, blackoutDatesByRoomRate],
  );

  const plansToApply = useMemo(() => applyBlockExclusions(plans, excludedBlockKeys), [plans, excludedBlockKeys]);

  const handleSelectBlackoutDates = (selectedIds: Array<string | number>) => {
    setSelectedBlackoutDates(blackoutDates.filter((blackoutDate) => selectedIds.includes(blackoutDate.sfid)));
  };

  const handleSelectRoomRates = (selectedIds: Array<string | number>) => {
    setSelectedRoomRates(roomRateRows.filter((row) => selectedIds.includes(row.id)));
  };

  const handleToggleBlockExclusion = (range: DeletedBlackoutRange) => {
    const key = getDeletedRangeKey(range);
    setExcludedBlockKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleStep = (nextStep: number) => {
    if (nextStep > step) {
      if (nextStep >= SELECT_ROOM_RATES_STEP && selectedBlackoutDates.length === 0) {
        setStepErrorMessage('Please select at least one blackout date.');
        setStepError(SELECT_BLACKOUT_DATES_STEP);
        return;
      }
      if (nextStep >= SUMMARY_STEP && selectedRoomRates.length === 0) {
        setStepErrorMessage('Please select at least one room rate.');
        setStepError(SELECT_ROOM_RATES_STEP);
        return;
      }
    }
    setStepError(null);
    setStepErrorMessage(null);
    setStep(nextStep);
  };

  const handleApply = () => {
    const payload = buildWizardApplyPayload(plansToApply);
    applyBlackoutDates.mutate(payload, {
      onSuccess: () => {
        // useApplyBlackoutDates invalidates ['blackoutDates', propertyId], which nothing here reads.
        queryClient.invalidateQueries({ queryKey: reservationQueryKeys.blackoutDatesByRoomRateAll(propertyId) });
        onClose();
      },
    });
  };

  const totalToCreate = plansToApply.reduce((sum, plan) => sum + plan.rangesToCreate.length, 0);
  const totalToDelete = plansToApply.reduce((sum, plan) => sum + plan.blocksToDelete.length, 0);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EventBusyOutlinedIcon color="warning" />
          <Typography variant="h6">Import Salesforce blackout dates</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', height: '75vh' }}>
        <Stepper nonLinear activeStep={step} sx={{ mb: 3 }}>
          {stepsList.map((label, index) => (
            <Step key={label}>
              <StepButton color="inherit" onClick={() => handleStep(index)}>
                <StepLabel error={stepError === index}>{label}</StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {stepErrorMessage && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {stepErrorMessage}
          </Typography>
        )}

        <Stack sx={{ flexGrow: 1, minHeight: 0 }}>
          {step === SELECT_BLACKOUT_DATES_STEP && (
            <BlackoutRecordSelection
              blackoutDates={blackoutDates}
              selectedBlackoutDates={selectedBlackoutDates}
              onSelectBlackoutDates={handleSelectBlackoutDates}
            />
          )}
          {step === SELECT_ROOM_RATES_STEP && (
            <RoomRateSelectionGrid
              roomRates={roomRateRows}
              propertyId={propertyId}
              selectedRoomRates={selectedRoomRates}
              onSelectRoomRates={handleSelectRoomRates}
              vendorId={vendorId}
            />
          )}
          {step === SUMMARY_STEP && (
            <BlackoutWizardSummary
              excludedBlockKeys={excludedBlockKeys}
              onToggleBlockExclusion={handleToggleBlockExclusion}
              plans={plans}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          {step === SUMMARY_STEP && (totalToCreate > 0 || totalToDelete > 0) && (
            <Typography variant="body2" color="text.secondary">
              <strong>{pluralizeToString('range', totalToCreate)}</strong> to create,{' '}
              <strong>{pluralizeToString('block', totalToDelete)}</strong> to remove
            </Typography>
          )}
        </Stack>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          {step > SELECT_BLACKOUT_DATES_STEP && (
            <Button variant="text" onClick={() => handleStep(step - 1)}>
              Back
            </Button>
          )}
          {step < SUMMARY_STEP && (
            <Button variant="contained" onClick={() => handleStep(step + 1)}>
              Continue
            </Button>
          )}
          {step === SUMMARY_STEP && (
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={applyBlackoutDates.isPending || totalToCreate + totalToDelete === 0}
            >
              {applyBlackoutDates.isPending ? 'Applying…' : 'Apply'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
